import React, { useEffect, useRef, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, formatApiError, hasWrite, isApprover, getTabCompany } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Download, AlertTriangle, HandCoins, Mail, FileText, Search, Pencil, Upload, FileDown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { LedgerImportPreview } from "../components/LedgerImportPreview";
import { UploadHistory } from "../components/UploadHistory";
import { askReason } from "../components/ApprovalLock";

const dateField = "h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";
const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const flabel = "block text-sm font-medium mb-1.5";

export default function CreditParties() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [reminders, setReminders] = useState(0);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("outstanding_desc");
  const canEdit = hasWrite(user);
  const canWrite = hasWrite(user);
  const [edit, setEdit] = useState(null);
  const [ef, setEf] = useState({ account_name: "", guarantor_name: "", guarantor_phone: "", guarantor_account: "", opening_balance: "", opening_type: "DR" });
  const [importFor, setImportFor] = useState(null);
  const impFileRef = useRef(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const bulkFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const emptyCf = { account_name: "", name: "", opening_balance: "", opening_type: "DR", credit_limit: "", phone: "", guarantor_name: "", guarantor_phone: "" };
  const [createOpen, setCreateOpen] = useState(false);
  const [cf, setCf] = useState(emptyCf);
  const saveCreate = async () => {
    const acc = cf.account_name.trim();
    if (!acc) return toast.error("Enter an account name");
    const name = cf.name.trim() || acc;
    const mag = Math.abs(parseFloat(cf.opening_balance) || 0);
    const ob = cf.opening_type === "CR" ? -mag : mag;
    try {
      const { data } = await api.post("/parties", {
        name, account_name: acc, is_credit_account: true,
        opening_balance: ob, opening_date: ob !== 0 ? new Date().toISOString().slice(0, 10) : "",
        credit_limit: parseFloat(cf.credit_limit) || 0, phone: cf.phone,
        guarantor_name: cf.guarantor_name, guarantor_phone: cf.guarantor_phone,
      });
      toast.success(data?.existing ? `Account "${acc}" already exists — it is shown in the list` : "Credit account created");
      setCreateOpen(false); setCf(emptyCf); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadBatches = async (pid) => setBatches((await api.get("/ledger/import-batches", { params: pid ? { party_id: pid } : {} })).data);

  const getTemplate = async (kind) => {
    try {
      const res = await api.get("/ledger/import-template", { params: { kind }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = kind === "bulk" ? "ledger_bulk_import_template.xlsx" : "ledger_import_template.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download template"); }
  };
  const impTemplate = () => getTemplate("single");
  const [pv, setPv] = useState(null);
  const runPreview = async (url, f, bulk, title, ref) => {
    const fd = new FormData();
    fd.append("file", f);
    setImporting(true);
    try {
      const { data } = await api.post(url, fd, { params: { preview: true }, headers: { "Content-Type": "multipart/form-data" } });
      setPv({ data, file: f, url, bulk, title });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); if (ref.current) ref.current.value = ""; }
  };
  const confirmImport = async (skip, rows) => {
    const fd = new FormData();
    fd.append("file", pv.file);
    if (rows) fd.append("rows_json", JSON.stringify(rows));
    try {
      const { data } = await api.post(pv.url, fd, { params: { skip_duplicates: !!skip }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Saved: ${data.debit_rows} due(s), ${data.credit_rows} payment(s)`);
      setPv(null); setImportFor(null); loadBatches(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doImport = (e) => {
    const f = e.target.files?.[0];
    if (f && importFor) runPreview(`/parties/${importFor.id}/import-ledger`, f, false, importFor.account_name || importFor.name, impFileRef);
  };

  const undoBatch = async (b) => {
    if (!window.confirm(`Undo this import? ${b.debit_rows} due(s) and ${b.credit_rows} payment(s) will be removed.`)) return;
    try {
      const { data } = await api.delete(`/ledger/import-batches/${b.batch_id}`);
      toast.success(`Removed ${data.removed_dues} due(s), ${data.removed_payments} payment(s)`);
      await loadBatches(importFor ? importFor.id : null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkTemplate = () => getTemplate("bulk");
  const doBulkImport = (e) => {
    const f = e.target.files?.[0];
    if (f) runPreview("/ledger/bulk-import", f, true, f.name, bulkFileRef);
  };

  const RecentImports = () => (
    batches.length > 0 ? (
      <div className="pt-2 border-t border-border mt-2">
        <div className="text-xs font-semibold text-muted-foreground mb-1.5">Recent imports</div>
        <div className="space-y-1 max-h-40 overflow-auto">
          {batches.map((b) => (
            <div key={b.batch_id} data-testid={`import-batch-${b.batch_id}`} className="flex items-center justify-between text-xs bg-muted/50 rounded-md px-2.5 py-1.5">
              <span className="truncate">{b.scope === "bulk" ? "Bulk · " : ""}{b.label ? b.label + " · " : ""}{b.debit_rows} due / {b.credit_rows} pay</span>
              <button data-testid={`undo-import-${b.batch_id}`} onClick={() => undoBatch(b)} className="text-destructive font-medium hover:underline shrink-0 ml-2">Undo</button>
            </div>
          ))}
        </div>
      </div>
    ) : null
  );

  const load = async () => {
    const { data } = await api.get("/credit-accounts");
    setParties(data.map((r) => ({ ...r, account_name: r.is_account ? r.account : "" })));
    setReminders(data.filter((p) => p.over_limit).length);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (importFor) loadBatches(importFor.id); }, [importFor]);
  useEffect(() => { if (bulkOpen) loadBatches(null); }, [bulkOpen]);

  const q = search.trim().toLowerCase();
  const matched = q
    ? parties.filter((p) => [p.name, p.account_name, p.phone, p.vehicle_number, p.guarantor_name, p.email]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : parties;
  const filtered = [...matched].sort((a, b) => {
    if (sort === "over_limit") {
      if (!!b.over_limit !== !!a.over_limit) return (b.over_limit ? 1 : 0) - (a.over_limit ? 1 : 0);
      return b.outstanding - a.outstanding;
    }
    if (sort === "outstanding_asc") return a.outstanding - b.outstanding;
    if (sort === "name_asc") return String(a.name).localeCompare(String(b.name), undefined, { numeric: true });
    return b.outstanding - a.outstanding; // outstanding_desc (default)
  });

  const download = async (p, format = "pdf") => {
    try {
      const params = { format };
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/parties/${p.id}/ledger`, { params, responseType: "blob" });
      const mime = format === "csv" ? "text/csv" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      const suffix = start || end ? `_${start || "start"}_to_${end || "today"}` : "";
      a.download = `ledger_${p.name.replace(/\s/g, "_")}${suffix}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Statement downloaded (${format.toUpperCase()})`);
    } catch {
      toast.error("Could not download statement");
    }
  };

  const downloadAccount = async (acc, format) => {
    try {
      const params = { account: acc, format };
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/accounts/ledger`, { params, responseType: "blob" });
      const mime = format === "csv" ? "text/csv" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `account_${acc.replace(/\s/g, "_")}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Account statement (${format.toUpperCase()})`);
    } catch { toast.error("Could not download account statement"); }
  };

  const emailStatement = async (p) => {    if (!p.email) return toast.error("Add an email on this account first (use the edit/guarantor dialog)");
    try {
      const { data } = await api.post(`/parties/${p.id}/email-ledger`);
      toast.success(`Statement emailed to ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteAccount = async (p) => {
    if (!window.confirm(`Delete account "${p.account || p.name}"? This is only possible because it has no transactions.`)) return;
    try {
      await api.delete("/credit-accounts", { params: { account: p.account || p.name } });
      toast.success("Account deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const total = parties.reduce((s, p) => s + p.outstanding, 0);
  const openEdit = (p) => {
    if (!canEdit) return;
    const ob0 = p.opening_balance || 0;
    setEf({ account_name: p.account_name || "", guarantor_name: p.guarantor_name || "", guarantor_phone: p.guarantor_phone || "", guarantor_account: p.guarantor_account || "", opening_balance: ob0 ? Math.abs(ob0) : "", opening_type: ob0 < 0 ? "CR" : "DR" });
    setEdit(p);
  };
  const saveEdit = async () => {
    try {
      const oldAcc = edit.account || edit.account_name || "";
      const mag = Math.abs(parseFloat(ef.opening_balance) || 0);
      const ob = ef.opening_type === "CR" ? -mag : mag;
      const edit_reason = askReason(user, "editing", `account ${oldAcc || edit.name}`);
      if (edit_reason === null) return;
      const res = await api.put("/accounts/details", { ...ef, party_id: edit.id, old: oldAcc, is_account: !!edit.is_account, opening_balance: ob, edit_reason });
      if (res.data?.status === "pending") toast.info(res.data.message);
      else toast.success("Account details updated");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout reminders={reminders}>
      <div className="p-8 max-w-6xl mx-auto">
        <PageHeader title="Credit Parties" subtitle="Grouped by credit account — combined outstanding per account; download statements for any period & track limits.">
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <div className="text-[11px] text-muted-foreground mb-0.5">From</div>
              <input data-testid="ledger-start" type="date" className={dateField} value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground mb-0.5">To</div>
              <input data-testid="ledger-end" type="date" className={dateField} value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            {(start || end) && (
              <button data-testid="ledger-clear-range" onClick={() => { setStart(""); setEnd(""); }}
                className="h-9 px-3 rounded-md border border-input bg-card text-xs hover:bg-muted transition-colors">Clear</button>
            )}
            {canWrite && (
              <button data-testid="new-credit-account-btn" onClick={() => { setCf(emptyCf); setCreateOpen(true); }}
                className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Plus size={15} /> New account</button>
            )}
            {canWrite && (
              <button data-testid="bulk-import-btn" onClick={() => setBulkOpen(true)}
                className="h-9 px-3 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"><Upload size={15} /> Bulk import</button>
            )}
            <UploadHistory canUndo={canWrite} onChanged={load} />
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-2">
              <div className="text-xs text-muted-foreground">Total Outstanding</div>
              <div className="num text-lg font-bold text-destructive">{inr(total)}</div>
            </div>
          </div>
        </PageHeader>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="credit-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by party, account, phone, vehicle or guarantor…"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          </div>
          <select data-testid="credit-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40">
            <option value="outstanding_desc">Highest outstanding</option>
            <option value="over_limit">Over-limit first</option>
            <option value="outstanding_asc">Lowest outstanding</option>
            <option value="name_asc">Name (A–Z)</option>
          </select>
        </div>

        {parties.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            <HandCoins className="mx-auto mb-3 opacity-40" size={32} />
            No parties on credit yet. Click <b className="text-foreground">New account</b> to add one, or credit invoices will appear here automatically.
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="credit-no-results" className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            No parties match “{search}”.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-x-auto animate-fade-up">
            <div className="min-w-[1040px]">
              <div className="grid grid-cols-[1.6fr_1fr_150px_110px_130px_300px] gap-4 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Account Name</span><span>Guarantor</span><span>A/C code</span><span className="text-right">Limit</span><span className="text-right">Outstanding</span><span className="text-right">Statement</span>
              </div>
              <div className="divide-y divide-border">
                {filtered.map((p) => (
                  <div key={p.id} data-testid={`credit-row-${p.id}`}
                    className="grid grid-cols-[1.6fr_1fr_150px_110px_130px_300px] gap-4 px-5 py-4 items-center hover:bg-background/50 transition-colors">
                    <div className="min-w-0">
                      {canEdit ? (
                        <button data-testid={`edit-acct-${p.id}`} onClick={() => openEdit(p)}
                          className="group inline-flex items-center gap-1 text-left max-w-full hover:text-primary transition-colors">
                          <span className="break-words font-medium">{p.account_name || "—"}</span>
                          <Pencil size={12} className="opacity-0 group-hover:opacity-70 shrink-0" />
                        </button>
                      ) : <span className="break-words font-medium block">{p.account_name || "—"}</span>}
                      {p.account_name ? (
                        <div className="flex gap-2 mt-0.5 text-[10px]">
                          <button data-testid={`acct-pdf-${p.id}`} onClick={() => downloadAccount(p.account_name, "pdf")} className="text-accent font-medium hover:underline">PDF</button>
                          <button data-testid={`acct-csv-${p.id}`} onClick={() => downloadAccount(p.account_name, "csv")} className="text-accent font-medium hover:underline">Excel</button>
                        </div>
                      ) : null}
                    </div>
                    <div className="min-w-0">
                      {canEdit ? (
                        <button data-testid={`edit-guarantor-${p.id}`} onClick={() => openEdit(p)}
                          className="group inline-flex items-center gap-1 text-left max-w-full hover:text-primary transition-colors">
                          <span className="break-words">{p.guarantor_name || "—"}</span>
                          <Pencil size={12} className="opacity-0 group-hover:opacity-70 shrink-0" />
                        </button>
                      ) : <span className="truncate block">{p.guarantor_name || "—"}</span>}
                      {p.guarantor_phone ? <div className="text-[10px] text-muted-foreground num truncate">{p.guarantor_phone}</div> : null}
                    </div>
                    <div className="min-w-0 cursor-pointer" data-testid={`credit-open-${p.id}`} onClick={() => window.open(`/credit/${p.id}?c=${getTabCompany()}`, "_blank")}>
                      <div className="font-medium flex flex-wrap items-center gap-2">
                        <span data-testid={`credit-accno-${p.id}`} className="num text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded shrink-0 hover:bg-primary/20 transition-colors">{p.account_no || "Open"}</span>
                        {p.over_limit && (
                          <span className="flex items-center gap-1 text-[10px] bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full shrink-0">
                            <AlertTriangle size={10} /> LIMIT
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground num truncate">{p.phone || "no phone"}{p.vehicle_number ? ` · ${p.vehicle_number}` : ""}</div>
                    </div>
                    <span className="num text-sm text-right text-muted-foreground">{p.credit_limit > 0 ? inr(p.credit_limit) : "—"}</span>
                    {p.advance > 0 ? (
                      <span data-testid={`credit-advance-${p.id}`} className="num text-sm text-right font-bold text-[hsl(var(--positive))]">{inr(p.advance)}<span className="text-[10px] font-normal"> adv</span></span>
                    ) : (
                      <span className={`num text-sm text-right font-bold ${p.over_limit ? "text-destructive" : "text-foreground"}`}>{inr(p.outstanding)}</span>
                    )}
                    <div className="justify-self-end flex items-center gap-1.5">
                      <button data-testid={`view-ledger-${p.id}`} onClick={() => navigate(`/credit/${p.id}`)}
                        className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-1.5 hover:bg-primary/90 transition-colors">
                        <FileText size={14} /> Ledger
                      </button>
                      {canWrite && (
                        <button data-testid={`import-ledger-${p.id}`} onClick={() => setImportFor(p)} title="Import manual ledger (CSV)"
                          className="h-9 w-9 rounded-md border border-input bg-card flex items-center justify-center hover:bg-muted transition-colors"><Upload size={14} /></button>
                      )}
                      <button data-testid={`download-ledger-${p.id}`} onClick={() => download(p, "pdf")} title="Ledger PDF"
                        className="h-9 w-9 rounded-md border border-input bg-card flex items-center justify-center hover:bg-muted transition-colors"><Download size={14} /></button>
                      <button data-testid={`download-ledger-csv-${p.id}`} onClick={() => download(p, "csv")} title="Ledger CSV"
                        className="h-9 px-2 rounded-md border border-input bg-card text-xs flex items-center hover:bg-muted transition-colors">CSV</button>
                      {canWrite && <button data-testid={`email-ledger-${p.id}`} onClick={() => emailStatement(p)} title={p.email ? `Email to ${p.email}` : "No email on file"}
                        className="h-9 w-9 rounded-md border border-input bg-card flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-40"
                        disabled={!p.email}><Mail size={14} /></button>}
                      {canWrite && !p.has_transactions && (
                        <button data-testid={`delete-account-${p.id}`} onClick={() => deleteAccount(p)} title="Delete account (no transactions)"
                          className="h-9 w-9 rounded-md border border-destructive/30 bg-card flex items-center justify-center text-destructive hover:bg-destructive/10 transition-colors"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">Edit · {edit?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className={flabel}>Account Name</label>
                <input data-testid="edit-party-account" className={field} value={ef.account_name}
                  onChange={(e) => setEf({ ...ef, account_name: e.target.value })} placeholder="Group / account name" />
              </div>
              <div>
                <label className={flabel}>Opening balance (₹) <span className="text-muted-foreground font-normal">— single balance for the whole account</span></label>
                <div className="flex gap-2">
                  <select data-testid="edit-party-opening-type" className={field.replace("w-full", "") + " !w-28 shrink-0"} value={ef.opening_type}
                    onChange={(e) => setEf({ ...ef, opening_type: e.target.value })}>
                    <option value="DR">DR (owes)</option>
                    <option value="CR">CR (advance)</option>
                  </select>
                  <input data-testid="edit-party-opening" type="number" min="0" className={field + " num flex-1"} value={ef.opening_balance}
                    onChange={(e) => setEf({ ...ef, opening_balance: e.target.value })} placeholder="0" />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{ef.opening_type === "CR" ? "Party has an advance with you (credit opening)." : "Party owes you this amount (debit opening)."}</p>
              </div>
              <div>
                <label className={flabel}>Guarantor Name</label>
                <input data-testid="edit-party-guarantor" className={field} value={ef.guarantor_name}
                  onChange={(e) => setEf({ ...ef, guarantor_name: e.target.value })} placeholder="Guarantor name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={flabel}>Guarantor Phone</label>
                  <input data-testid="edit-party-guarantor-phone" className={field + " num"} value={ef.guarantor_phone}
                    onChange={(e) => setEf({ ...ef, guarantor_phone: e.target.value })} placeholder="optional" />
                </div>
                <div>
                  <label className={flabel}>Guarantor Account</label>
                  <input data-testid="edit-party-guarantor-account" className={field} value={ef.guarantor_account}
                    onChange={(e) => setEf({ ...ef, guarantor_account: e.target.value })} placeholder="optional" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <button data-testid="edit-party-save" onClick={saveEdit}
                className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">Save changes</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">New credit account</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className={flabel}>Account name <span className="text-destructive">*</span></label>
                <input data-testid="create-account-name" className={field} value={cf.account_name}
                  onChange={(e) => setCf({ ...cf, account_name: e.target.value })} placeholder="e.g. NICE / Sharma Fleet" />
              </div>
              <div>
                <label className={flabel}>Party name <span className="text-muted-foreground font-normal">(optional — defaults to account name)</span></label>
                <input data-testid="create-party-name" className={field} value={cf.name}
                  onChange={(e) => setCf({ ...cf, name: e.target.value })} placeholder="Business / person name" />
              </div>
              <div>
                <label className={flabel}>Opening balance (₹) <span className="text-muted-foreground font-normal">— optional</span></label>
                <div className="flex gap-2">
                  <select data-testid="create-opening-type" className={field.replace("w-full", "") + " !w-28 shrink-0"} value={cf.opening_type}
                    onChange={(e) => setCf({ ...cf, opening_type: e.target.value })}>
                    <option value="DR">DR (owes)</option>
                    <option value="CR">CR (advance)</option>
                  </select>
                  <input data-testid="create-opening" type="number" min="0" className={field + " num flex-1"} value={cf.opening_balance}
                    onChange={(e) => setCf({ ...cf, opening_balance: e.target.value })} placeholder="0" />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{cf.opening_type === "CR" ? "Party has an advance with you (credit opening)." : "Party owes you this amount (debit opening)."}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={flabel}>Credit limit (₹)</label>
                  <input data-testid="create-credit-limit" type="number" min="0" className={field + " num"} value={cf.credit_limit}
                    onChange={(e) => setCf({ ...cf, credit_limit: e.target.value })} placeholder="optional" />
                </div>
                <div>
                  <label className={flabel}>Phone</label>
                  <input data-testid="create-phone" className={field + " num"} value={cf.phone}
                    onChange={(e) => setCf({ ...cf, phone: e.target.value })} placeholder="optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={flabel}>Guarantor name</label>
                  <input data-testid="create-guarantor" className={field} value={cf.guarantor_name}
                    onChange={(e) => setCf({ ...cf, guarantor_name: e.target.value })} placeholder="optional" />
                </div>
                <div>
                  <label className={flabel}>Guarantor phone</label>
                  <input data-testid="create-guarantor-phone" className={field + " num"} value={cf.guarantor_phone}
                    onChange={(e) => setCf({ ...cf, guarantor_phone: e.target.value })} placeholder="optional" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <button data-testid="create-account-save" onClick={saveCreate}
                className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">Create account</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!importFor} onOpenChange={(o) => !o && setImportFor(null)}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">Import ledger · {importFor?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">Upload an Excel (.xlsx) or CSV sheet from your manual khata. Columns: <b className="text-foreground">date, particulars, debit, credit</b>. Download the Template to see the format — your own sheet also works if its headings say Date, Narration, Dr, Cr. You'll see a preview with totals before anything is saved.</p>
              <ul className="text-muted-foreground list-disc pl-5 space-y-1">
                <li><b className="text-foreground">debit</b> = amount taken on credit / goods given (increases dues)</li>
                <li><b className="text-foreground">credit</b> = payment the party made (reduces dues)</li>
                <li>Imported rows are marked <b className="text-foreground">MANUAL</b> and flow into this account's ledger &amp; outstanding by date.</li>
              </ul>
              <input ref={impFileRef} type="file" accept=".csv,.xlsx" onChange={doImport} className="hidden" data-testid="import-ledger-input" />
              <div className="flex gap-2 pt-1">
                <button data-testid="import-ledger-template" onClick={impTemplate}
                  className="h-10 px-3 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"><FileDown size={15} /> Template</button>
                <button data-testid="import-ledger-upload" onClick={() => impFileRef.current?.click()} disabled={importing}
                  className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"><Upload size={16} /> {importing ? "Reading…" : "Choose file"}</button>
              </div>
              <RecentImports />
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">Bulk import ledger</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">Migrate your whole khata in one Excel (.xlsx) or CSV sheet — download the Template for the format; a preview with totals per account appears before saving. Columns: <b className="text-foreground">party_name, account_name, date, particulars, debit, credit</b>. Each row targets the party (grouped under its account); <b className="text-foreground">debit</b> = dues, <b className="text-foreground">credit</b> = payments. Rows are marked <b className="text-foreground">MANUAL</b>.</p>
              <input ref={bulkFileRef} type="file" accept=".csv,.xlsx" onChange={doBulkImport} className="hidden" data-testid="bulk-import-input" />
              <div className="flex gap-2 pt-1">
                <button data-testid="bulk-import-template" onClick={bulkTemplate}
                  className="h-10 px-3 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"><FileDown size={15} /> Template</button>
                <button data-testid="bulk-import-upload" onClick={() => bulkFileRef.current?.click()} disabled={importing}
                  className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"><Upload size={16} /> {importing ? "Reading…" : "Choose file"}</button>
              </div>
              <RecentImports />
            </div>
          </DialogContent>
        </Dialog>

        {pv && <LedgerImportPreview preview={pv.data} bulk={pv.bulk} title={pv.title} onCancel={() => setPv(null)} onConfirm={confirmImport} />}

        {reminders > 0 && (
          <div className="mt-6 rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm">
            <div className="flex items-center gap-2 font-semibold text-destructive mb-1">
              <AlertTriangle size={16} /> In-app reminders
            </div>
            <p className="text-muted-foreground">
              {reminders} party credit limit{reminders > 1 ? "s" : ""} exceeded. Follow up for recovery.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
